import type { CSSProperties } from 'react';

export type MartinaThinkingStage =
  | 'reading'
  | 'checking'
  | 'comparing'
  | 'writing'
  | 'waiting';

const DEFAULT_MESSAGES: Record<MartinaThinkingStage, string> = {
  reading: 'Martina sta leggendo la richiesta…',
  checking: 'Martina sta verificando memoria e fonti collegate…',
  comparing: 'Martina sta confrontando le evidenze…',
  writing: 'Martina sta completando la risposta…',
  waiting: 'Martina sta completando la verifica. Potrebbe richiedere ancora qualche secondo…',
};

type MartinaThinkingIndicatorProps = {
  message?: string;
  stage?: MartinaThinkingStage;
  className?: string;
  centered?: boolean;
};

/**
 * Indicatore canonico per le risposte Martina.
 *
 * Non sostituisce gli skeleton del CRM: comunica che Martina sta elaborando
 * una risposta, leggendo fonti o preparando una proposta. Un solo componente
 * evita loader e messaggi divergenti nelle diverse superfici Martina.
 */
export function MartinaThinkingIndicator({
  message,
  stage = 'checking',
  className = '',
  centered = false,
}: MartinaThinkingIndicatorProps) {
  const label = message ?? DEFAULT_MESSAGES[stage];
  const layout = centered ? 'justify-center text-center' : '';

  return (
    <div
      className={`flex items-center gap-2 text-sm text-muted-foreground ${layout} ${className}`.trim()}
      role="status"
      aria-live="polite"
      data-testid="martina-thinking-indicator"
    >
      <span className="inline-flex shrink-0 items-center gap-1" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <span
            key={index}
            className="h-1.5 w-1.5 rounded-lg bg-primary motion-safe:animate-pulse motion-reduce:animate-none"
            style={{ animationDelay: `${index * 120}ms` } as CSSProperties}
          />
        ))}
      </span>
      <span>{label}</span>
    </div>
  );
}

export { DEFAULT_MESSAGES as MARTINA_THINKING_MESSAGES };
